import type { CSSProperties, ReactNode } from "react";

import { cx } from "@/lib/cx";
import { enterProps } from "@/ui/motion";

/**
 * A placeholder block in the shape of the thing that is loading.
 *
 * Every one of the four screens knows its own layout before the data lands, so
 * every one of them shows that layout rather than a spinner in an empty box.
 * The reason is not decoration: a spinner says "wait", a skeleton says "here is
 * what is coming", and it holds the height so the page does not jump the moment
 * the query resolves. The stats page always did this; the other three used to
 * flash a spinner instead, which is why the app felt like four apps.
 *
 * `aria-busy` and one label belong on the *container* — see {@link SkeletonList}
 * — never on the individual blocks, which are decoration and are hidden.
 */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <span
      {...enterProps("skeleton", {
        // A query that resolves inside this small window unmounts the
        // placeholder before it paints. That prevents a fast cache hit from
        // flashing a skeleton between the old content and the real result.
        // The pulse still starts immediately once the placeholder is visible;
        // there is deliberately no shimmer layer competing with it.
        style: { ...style, animationDelay: "calc(80ms * var(--busee-motion-scale))" },
      })}
      aria-hidden
      // `bg-fill-2` rather than the opaque `bg-sunken`: the fill tiers are alpha
      // and composite over whatever is behind them, so one placeholder is
      // correct on a panel, inside a grouped card and on a blurred sheet. The
      // opaque token drew a darker-than-the-page block on every surface that
      // was not `bg-surface`.
      className={cx("block rounded-control", className)}
    >
      {/* The scaled pulse is intentionally on the child. Its animation
          shorthand and the enter variant both own `animation-name`, so they
          cannot share an element without one cancelling the other. */}
      <span
        aria-hidden
        className="busee-motion-pulse block size-full bg-fill-2"
        style={{ borderRadius: "inherit" }}
      />
    </span>
  );
}

export type SkeletonListProps = {
  /** Announced while the placeholder is on screen, e.g. "Loading tasks". */
  label: string;
  className?: string;
  children: ReactNode;
};

/** Wraps a set of {@link Skeleton} blocks with the one status announcement. */
export function SkeletonList({ label, className, children }: SkeletonListProps) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={className}>
      {children}
    </div>
  );
}

export type SkeletonRowsProps = {
  /** Announced while the placeholder is on screen, e.g. "Loading tasks". */
  label: string;
  /** How many rows the real list is likely to open with. */
  count?: number;
  className?: string;
};

/**
 * The list-shaped skeleton, at the real row height.
 *
 * Every feature was drawing its own stack of blocks at whatever height looked
 * right, and all of them predated the density scale — so the placeholder was
 * roughly twice as tall as the list that replaced it and the page jumped
 * upward the moment the query landed, which is the exact failure a skeleton
 * exists to prevent. `min-h-row` tracks the surrounding `[data-density]`, so
 * this stays correct if a list is switched to comfortable.
 */
export function SkeletonRows({ label, count = 6, className }: SkeletonRowsProps) {
  return (
    <SkeletonList label={label} className={cx("flex flex-col", className)}>
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className="flex row-height items-center gap-3 px-2">
          <Skeleton className="size-5 shrink-0 rounded-full" />
          <Skeleton
            className="h-3.5 rounded-full"
            // A list of identical bars reads as a table, not as text, so the
            // widths walk a short repeating cycle.
            style={{ width: `${[62, 45, 74, 38, 56, 68][index % 6]}%` }}
          />
        </span>
      ))}
    </SkeletonList>
  );
}
